import { useState, useEffect, useCallback, useRef } from 'react'
import { HiX, HiUserAdd, HiSearch, HiTrash, HiShieldCheck, HiUserGroup } from 'react-icons/hi'
import { projectsApi, authApi } from '../../api/client'
import { useAuth } from '../../hooks/useAuth'

const ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  member: 'Member',
  viewer: 'Viewer',
}

const ROLE_COLORS = {
  owner: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  admin: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  editor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  member: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  viewer: 'bg-surface-100 text-surface-700 dark:bg-surface-700 dark:text-surface-300',
}

const ASSIGNABLE_ROLES = ['admin', 'editor', 'member', 'viewer']

function Avatar({ user, size = 'md' }) {
  const sizeClass = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'
  if (user.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={user.display_name}
        className={`${sizeClass} rounded-full object-cover`}
      />
    )
  }
  const initials = (user.display_name || user.email || '?')
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  return (
    <div className={`${sizeClass} rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 flex items-center justify-center font-medium`}>
      {initials}
    </div>
  )
}

export default function ProjectMembersPanel({ project, onClose }) {
  const { user: currentUser, isAdmin } = useAuth()
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddMember, setShowAddMember] = useState(false)
  const [orgUsers, setOrgUsers] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [addingUserId, setAddingUserId] = useState(null)
  const [addRole, setAddRole] = useState('member')
  const [removingId, setRemovingId] = useState(null)
  const [updatingId, setUpdatingId] = useState(null)
  const searchRef = useRef(null)

  // Check if current user can manage members (owner, admin on project, or org admin)
  const currentMembership = members.find(m => m.user_id === currentUser?.id)
  const canManage = isAdmin || ['owner', 'admin'].includes(currentMembership?.role)

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await projectsApi.getMembers(project.id)
      setMembers(data)
    } catch (err) {
      console.error('Failed to fetch project members:', err)
    } finally {
      setLoading(false)
    }
  }, [project.id])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  // Fetch org users when Add Member is opened
  useEffect(() => {
    if (showAddMember && orgUsers.length === 0) {
      authApi.listUsers().then(({ data }) => setOrgUsers(data)).catch(console.error)
    }
    if (showAddMember) {
      setTimeout(() => searchRef.current?.focus(), 100)
    }
  }, [showAddMember])

  const handleAddMember = async (userId) => {
    try {
      setAddingUserId(userId)
      await projectsApi.addMember(project.id, userId, addRole)
      await fetchMembers()
      setSearchQuery('')
      setAddRole('member')
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add member'
      alert(msg)
    } finally {
      setAddingUserId(null)
    }
  }

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from this project?`)) return
    try {
      setRemovingId(memberId)
      await projectsApi.removeMember(project.id, memberId)
      setMembers(prev => prev.filter(m => m.id !== memberId))
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to remove member'
      alert(msg)
    } finally {
      setRemovingId(null)
    }
  }

  const handleRoleChange = async (memberId, newRole) => {
    try {
      setUpdatingId(memberId)
      await projectsApi.updateMember(project.id, memberId, newRole)
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m))
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update role'
      alert(msg)
    } finally {
      setUpdatingId(null)
    }
  }

  // Filter org users to show only non-members matching search
  const memberUserIds = new Set(members.map(m => m.user_id))
  const filteredUsers = orgUsers.filter(u => {
    if (memberUserIds.has(u.id)) return false
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      u.display_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    )
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-end z-50" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white dark:bg-surface-800 h-full shadow-xl flex flex-col animate-slide-in-right"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-2">
            <HiUserGroup className="w-5 h-5 text-brand-600" />
            <h2 className="text-lg font-semibold text-surface-800 dark:text-surface-200">
              Project Members
            </h2>
            <span className="text-sm text-surface-500 dark:text-surface-400">
              ({members.length})
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700 rounded"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* Add Member Button */}
        {canManage && !showAddMember && (
          <div className="px-6 pt-4">
            <button
              onClick={() => setShowAddMember(true)}
              className="btn btn-primary w-full"
            >
              <HiUserAdd className="w-4 h-4 mr-2" />
              Add Member
            </button>
          </div>
        )}

        {/* Add Member Section */}
        {showAddMember && (
          <div className="px-6 pt-4 pb-2 border-b border-surface-200 dark:border-surface-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-surface-700 dark:text-surface-300">Add Member</h3>
              <button
                onClick={() => { setShowAddMember(false); setSearchQuery('') }}
                className="text-xs text-surface-500 hover:text-surface-700 dark:hover:text-surface-300"
              >
                Cancel
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-surface-300 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-surface-800 dark:text-surface-200 placeholder-surface-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>

            {/* Role selector */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-surface-500 dark:text-surface-400">Role:</span>
              <select
                value={addRole}
                onChange={e => setAddRole(e.target.value)}
                className="text-xs border border-surface-300 dark:border-surface-600 rounded-md px-2 py-1 bg-white dark:bg-surface-700 text-surface-800 dark:text-surface-200"
              >
                {ASSIGNABLE_ROLES.map(r => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>
            </div>

            {/* User list */}
            <div className="max-h-48 overflow-y-auto space-y-1 mb-3">
              {filteredUsers.length === 0 ? (
                <p className="text-sm text-center text-surface-500 dark:text-surface-400 py-4">
                  {searchQuery ? 'No matching users found' : 'All org members are already in this project'}
                </p>
              ) : (
                filteredUsers.slice(0, 20).map(u => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar user={u} size="sm" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-surface-800 dark:text-surface-200 truncate">
                          {u.display_name}
                        </p>
                        <p className="text-xs text-surface-500 dark:text-surface-400 truncate">
                          {u.email}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddMember(u.id)}
                      disabled={addingUserId === u.id}
                      className="shrink-0 ml-2 px-3 py-1 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md disabled:opacity-50 transition-colors"
                    >
                      {addingUserId === u.id ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-center text-surface-500 dark:text-surface-400 py-8">No members yet</p>
          ) : (
            <div className="space-y-1">
              {/* Sort: owner first, then alphabetically */}
              {[...members]
                .sort((a, b) => {
                  if (a.role === 'owner') return -1
                  if (b.role === 'owner') return 1
                  return (a.user?.display_name || '').localeCompare(b.user?.display_name || '')
                })
                .map(member => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-700/50 transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar user={member.user} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-surface-800 dark:text-surface-200 truncate">
                            {member.user?.display_name}
                          </p>
                          {member.user_id === currentUser?.id && (
                            <span className="text-[10px] font-medium text-surface-500 dark:text-surface-400">(you)</span>
                          )}
                        </div>
                        <p className="text-xs text-surface-500 dark:text-surface-400 truncate">
                          {member.user?.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* Role badge / selector */}
                      {canManage && member.role !== 'owner' ? (
                        <select
                          value={member.role}
                          onChange={e => handleRoleChange(member.id, e.target.value)}
                          disabled={updatingId === member.id}
                          className={`text-xs font-medium rounded-full px-2 py-0.5 border-0 cursor-pointer disabled:opacity-50 ${ROLE_COLORS[member.role]}`}
                        >
                          {ASSIGNABLE_ROLES.map(r => (
                            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`text-xs font-medium rounded-full px-2.5 py-0.5 ${ROLE_COLORS[member.role]}`}>
                          {member.role === 'owner' && <HiShieldCheck className="w-3 h-3 inline mr-0.5 -mt-0.5" />}
                          {ROLE_LABELS[member.role]}
                        </span>
                      )}

                      {/* Remove button */}
                      {canManage && member.role !== 'owner' && (
                        <button
                          onClick={() => handleRemoveMember(member.id, member.user?.display_name)}
                          disabled={removingId === member.id}
                          className="p-1 text-surface-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                          title="Remove member"
                        >
                          <HiTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="px-6 py-3 border-t border-surface-200 dark:border-surface-700">
          <p className="text-xs text-surface-500 dark:text-surface-400">
            {canManage
              ? 'You can add members, change roles, and remove members.'
              : 'Only project owners and admins can manage members.'}
          </p>
        </div>
      </div>
    </div>
  )
}
